import { useRef, useState } from 'react'
import { ChartShell, ChartTooltip, colorVar, formatChartValue, useTweened, useWidth, type ChartColor, type ChartUnit } from './chartKit'

export interface DonutSlice {
  id: string
  label: string
  value: number
  color?: ChartColor | undefined
}

export interface DonutChartProps {
  slices: DonutSlice[]
  unit?: ChartUnit | undefined
  /** Testo sotto il totale al centro (default "Totale"). */
  totalLabel?: string | undefined
  loading?: boolean | undefined
  label: string
  emptyTitle?: string | undefined
  emptyDescription?: string | undefined
}

const SIZE = 180
const R = 72
const TH = 26

function arc(a0: number, a1: number): string {
  const c = SIZE / 2
  const full = a1 - a0 >= Math.PI * 2 - 1e-4
  const e = full ? a0 + Math.PI * 2 - 1e-3 : a1
  const p = (a: number, r: number) => `${c + r * Math.sin(a)},${c - r * Math.cos(a)}`
  const large = e - a0 > Math.PI ? 1 : 0
  return `M${p(a0, R)} A${R},${R} 0 ${large} 1 ${p(e, R)} L${p(e, R - TH)} A${R - TH},${R - TH} 0 ${large} 0 ${p(a0, R - TH)}Z`
}

export function DonutChart(p: DonutChartProps) {
  const { slices, unit = 'number' } = p
  const rows = slices.filter(s => s.value > 0)
  const total = rows.reduce((a, s) => a + s.value, 0)
  const [active, setActive] = useState<number | null>(null)
  const touch = useRef(false)
  const host = useRef<HTMLDivElement>(null)
  const width = useWidth(host)
  const tween = useTweened(rows.map(s => s.id), rows.map(s => s.value))
  const tTotal = tween.reduce((a, v) => a + v, 0) || 1
  const pct = (v: number) => formatChartValue((v / (total || 1)) * 100, 'percent', 1)
  const table = {
    caption: p.label,
    head: ['Voce', 'Valore', 'Quota'],
    rows: rows.map(s => ({ key: s.id, cells: [s.label, formatChartValue(s.value, unit), pct(s.value)] })),
  }
  const summary = `${p.label}. Totale ${formatChartValue(total, unit)}. ${rows.map(s => `${s.label}: ${formatChartValue(s.value, unit)} (${pct(s.value)})`).join('; ')}.`
  let acc = 0
  const arcs = rows.map((s, i) => {
    const a0 = (acc / tTotal) * Math.PI * 2
    acc += tween[i] ?? 0
    return { a0, a1: (acc / tTotal) * Math.PI * 2, s, i }
  })

  return (
    <div ref={host} className={`md-donut${width >= 480 ? ' md-donut--wide' : ''}`}>
      <ChartShell
        loading={p.loading} empty={rows.length === 0} emptyTitle={p.emptyTitle} emptyDescription={p.emptyDescription}
        ariaLabel={summary} table={table} height={SIZE}
        count={rows.length} active={active} onActive={setActive} onLeave={() => { if (!touch.current) setActive(null) }}
        announce={active == null ? '' : `${rows[active]?.label}: ${formatChartValue(rows[active]?.value ?? 0, unit)}, ${pct(rows[active]?.value ?? 0)}`}
        tooltip={active == null || !rows[active] ? null : (
          <ChartTooltip x={SIZE / 2} y={SIZE / 2 - 10} width={SIZE} title={rows[active]!.label} rows={[{ label: pct(rows[active]!.value), value: formatChartValue(rows[active]!.value, unit), color: colorVar(rows[active]!.color, active) }]} />
        )}
      >
        {() => (
          <svg className="md-chart__svg" width={SIZE} height={SIZE} aria-hidden="true">
            {arcs.map(({ a0, a1, s, i }) => (
              <path
                key={s.id} d={arc(a0, a1)} fill={colorVar(s.color, i)} stroke="hsl(var(--md-surface))" strokeWidth={2}
                opacity={active == null || active === i ? 1 : 0.45}
                onPointerMove={e => { touch.current = e.pointerType === 'touch'; setActive(i) }}
                onPointerDown={e => { touch.current = e.pointerType === 'touch'; setActive(i) }}
              />
            ))}
            <text className="md-donut__total md-donut__center" x={SIZE / 2} y={SIZE / 2 - 2}>{formatChartValue(total, unit, unit === 'eur' ? 0 : undefined)}</text>
            <text className="md-chart__text md-donut__center" x={SIZE / 2} y={SIZE / 2 + 16}>{p.totalLabel ?? 'Totale'}</text>
          </svg>
        )}
      </ChartShell>
      {rows.length > 0 && !p.loading ? (
        <ul className="md-donut__list" aria-hidden="true">
          {rows.map((s, i) => (
            <li key={s.id} className="md-donut__item" data-active={active === i}>
              <span className="md-chart__swatch" style={{ background: colorVar(s.color, i) }} />
              <span className="md-donut__name">{s.label}</span>
              <span className="md-num">{formatChartValue(s.value, unit)}</span>
              <span className="md-num md-chart__text">{pct(s.value)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
